import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatDialogRef } from '@angular/material/dialog';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { switchMap, take } from 'rxjs';
import { ApiService } from '../../../../../api/services/api.service';
import {
  SurveyAnswerDto,
  SurveyDto,
  SurveyOptionDto,
  SurveyQuestionDto,
} from '../../../../../api/models/fraud.models';
import { IsShowErrorPipe } from '../../../../pipes/is-show-error.pipe';
import { FieldErrorComponent } from '../../../field-error/field-error.component';
import { NgxTrimDirective } from '../../../../directives/ngx-trim.directive';
import { VariablesService } from '../../../../services/variables.service';
import { BonusSurveyService } from '../../../../services/bonus-survey.service';
import {
  buildAnswerPath,
  buildClearedSurveyAnswer,
  buildSurveyAnswer,
  findResumeQuestionId,
  getRemainingChainLength,
  getSurveyQuestionById,
  getSurveyQuestions,
  isCustomTextOptionSelected,
  isSingleSelectQuestionType,
  isSurveyAnswerProvided,
  questionHasIconOptions,
  resolveNextQuestionId,
  sanitizeSurveyIconSvg,
} from '../../../../utils/survey.utils';
import { CutNumberPipe } from '../../../../pipes/cut-number.pipe';
import { MatIcon } from '@angular/material/icon';

type StepControlValue = string | number | number[];

@Component({
  selector: 'app-bonus-survey-stepper',
  imports: [
    NgClass,
    ReactiveFormsModule,
    MatFormField,
    MatSelect,
    MatOption,
    IsShowErrorPipe,
    FieldErrorComponent,
    NgxTrimDirective,
    CutNumberPipe,
    MatIcon,
  ],
  templateUrl: './bonus-survey-stepper.component.html',
  styleUrl: './bonus-survey-stepper.component.scss',
})
export class BonusSurveyStepperComponent implements OnInit {
  readonly surveyId = input.required<number>();
  readonly surveyLoaded = output<SurveyDto>();
  readonly surveyLoadFinished = output<void>();

  private readonly _fb = inject(NonNullableFormBuilder);
  private readonly _dialogRef = inject(MatDialogRef);
  private readonly _apiService = inject(ApiService);
  private readonly _variablesService = inject(VariablesService);
  private readonly _bonusSurveyService = inject(BonusSurveyService);
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _iconHtmlCache = new Map<string, SafeHtml>();

  readonly surveyViewState = signal<'loading' | 'error' | 'completed' | 'active'>('loading');
  readonly loadError = signal<string | null>(null);
  readonly completedRewardAmount = signal<string | number>('');
  /** Transient flags while `surveyViewState === 'active'`. */
  readonly isSaving = signal(false);
  readonly validationError = signal<string | null>(null);

  readonly isReadOnly = computed(() => this.surveyViewState() === 'completed');

  private readonly _survey = signal<SurveyDto | null>(null);
  readonly survey = this._survey.asReadonly();
  private readonly _answers = signal<ReadonlyMap<number, SurveyAnswerDto>>(new Map());
  readonly currentQuestionId = signal<number | null>(null);
  readonly visitedQuestionIds = signal<number[]>([]);

  readonly stepControl = this._fb.control<StepControlValue>('', Validators.required);
  readonly customTextControl = this._fb.control('');
  private readonly _customTextField = viewChild<ElementRef<HTMLElement>>('customTextField');
  private readonly _stepControlValue = toSignal(this.stepControl.valueChanges, {
    initialValue: this.stepControl.value as StepControlValue,
  });
  private readonly _customTextValue = toSignal(this.customTextControl.valueChanges, {
    initialValue: this.customTextControl.value,
  });

  readonly currentQuestion = computed(() => {
    const survey = this._survey();
    const questionId = this.currentQuestionId();
    if (!survey || questionId === null) {
      return null;
    }

    return getSurveyQuestionById(survey, questionId) ?? null;
  });

  /** Position of the current question inside the branch the user actually walked. */
  readonly currentStepNumber = computed(() => {
    const questionId = this.currentQuestionId();
    if (questionId == null) {
      return 0;
    }

    const index = this.visitedQuestionIds().indexOf(questionId);
    return index >= 0 ? index + 1 : 1;
  });

  /** Length of the current branch, re-estimated as soon as the answer picks a branch. */
  readonly totalQuestions = computed(() => {
    const survey = this._survey();
    const question = this.currentQuestion();
    if (!survey || !question) {
      return getSurveyQuestions(survey).length;
    }

    this._stepControlValue();
    this._customTextValue();
    const answer = buildSurveyAnswer(question, this.stepControl.value, this.customTextControl.value);
    const stepsBefore = Math.max(this.currentStepNumber() - 1, 0);

    return stepsBefore + getRemainingChainLength(survey, question, answer, this._answers());
  });

  readonly progressSegmentIndexes = computed(() =>
    Array.from({ length: this.totalQuestions() }, (_, index) => index),
  );

  readonly showIconGrid = computed(() => {
    const question = this.currentQuestion();
    // DROP_DOWN ignores option icons and always renders as a plain select.
    if (!question || question.type === 'DROP_DOWN') {
      return false;
    }

    return questionHasIconOptions(question);
  });

  readonly isCustomTextSelected = computed(() => {
    this._stepControlValue();
    return isCustomTextOptionSelected(this.currentQuestion(), this.stepControl.value);
  });

  /** True when the current step has a valid answer (or is optional and empty). */
  readonly isStepValid = computed(() => {
    const question = this.currentQuestion();
    if (!question) {
      return false;
    }

    const value = this._stepControlValue();
    const customText = this._customTextValue();

    if (question.required && !this._isStepAnswerProvided(question, value, customText)) {
      return false;
    }

    if (isCustomTextOptionSelected(question, value) && !String(customText).trim()) {
      return false;
    }

    return true;
  });

  readonly isAtEnd = computed(() => {
    const survey = this._survey();
    const question = this.currentQuestion();
    if (!survey || !question) {
      return false;
    }

    const value = this._stepControlValue();
    const customText = this._customTextValue();
    if (question.required && !this._isStepAnswerProvided(question, value, customText)) {
      return false;
    }

    const answer = buildSurveyAnswer(question, value, customText);
    return resolveNextQuestionId(survey, question, answer) === null;
  });

  constructor() {
    effect(() => {
      this._customTextField()?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    });
  }

  ngOnInit(): void {
    this._loadSurvey();
  }

  isCheckboxOptionSelected(optionId: number): boolean {
    const value = this.stepControl.value;
    return Array.isArray(value) && value.includes(optionId);
  }

  getOptionIconHtml(option: SurveyOptionDto): SafeHtml | null {
    const sanitized = sanitizeSurveyIconSvg(option.iconSvg);
    if (!sanitized) {
      return null;
    }

    const cached = this._iconHtmlCache.get(sanitized);
    if (cached) {
      return cached;
    }

    const safe = this._sanitizer.bypassSecurityTrustHtml(sanitized);
    this._iconHtmlCache.set(sanitized, safe);
    return safe;
  }

  toggleCheckboxOption(optionId: number): void {
    if (this.isReadOnly()) {
      return;
    }

    const current = Array.isArray(this.stepControl.value) ? [...this.stepControl.value] : [];
    const optionIndex = current.indexOf(optionId);

    if (optionIndex >= 0) {
      current.splice(optionIndex, 1);
    } else {
      current.push(optionId);
    }

    this.stepControl.setValue(current);
    this.stepControl.markAsDirty();
    this._syncCustomTextValidators();
  }

  onSingleChoiceChange(): void {
    this._syncCustomTextValidators();
  }

  onBack(): void {
    const path = this.visitedQuestionIds();
    if (path.length <= 1) {
      return;
    }

    // Persist in-progress edits (including cleared multi-choice) to the server.
    this._syncCurrentAnswerToServer();

    const previousQuestionId = path[path.length - 2];
    this.visitedQuestionIds.set(path.slice(0, -1));
    this.currentQuestionId.set(previousQuestionId);
    this.validationError.set(null);
    this._syncControlWithCurrentQuestion();
  }

  onContinue(): void {
    if (this.isReadOnly() || this.isSaving()) {
      return;
    }

    const question = this.currentQuestion();
    const survey = this._survey();
    if (!question || !survey) {
      return;
    }

    if (!this._validateCurrentStep(question)) {
      return;
    }

    const answer = buildSurveyAnswer(question, this.stepControl.value, this.customTextControl.value);
    this._storeAnswer(question.id, answer);
    this.validationError.set(null);
    this.isSaving.set(true);

    this._apiService.postSurveyAnswers(survey.id, { answers: [answer] }).pipe(take(1)).subscribe({
      next: () => {
        this._bonusSurveyService.invalidateSurveyCache(survey.id);
        const nextQuestionId = resolveNextQuestionId(survey, question, answer);
        this.isSaving.set(false);

        if (nextQuestionId === null) {
          return;
        }

        this._goToQuestion(nextQuestionId);
      },
      error: (error) => {
        this.isSaving.set(false);

        if (error?.status === 409 || error?.error?.code === 41) {
          this._handleSurveyAlreadyCompleted(survey);
          return;
        }

        this.validationError.set(error?.error?.message ?? 'Unable to save your answer. Please try again.');
      },
    });
  }

  onFinish(): void {
    if (this.isReadOnly() || this.isSaving()) {
      return;
    }

    const survey = this._survey();
    const question = this.currentQuestion();
    if (!survey || !question) {
      return;
    }

    if (!this._validateCurrentStep(question)) {
      return;
    }

    const answer = buildSurveyAnswer(question, this.stepControl.value, this.customTextControl.value);
    this._storeAnswer(question.id, answer);
    this.isSaving.set(true);
    this.validationError.set(null);

    this._apiService.postSurveyAnswers(survey.id, { answers: [answer] }).pipe(
      take(1),
      switchMap(() => this._apiService.completeSurvey(survey.id)),
    ).subscribe({
      next: (response) => {
        this._bonusSurveyService.invalidateSurveyCache(survey.id);
        this.isSaving.set(false);
        this.completedRewardAmount.set(response.rewardAmount);
        this.surveyViewState.set('completed');
        this._syncControlDisabledState();
        this._variablesService.triggerBannersReload();
      },
      error: (error) => {
        this.isSaving.set(false);

        if (error?.status === 409 || error?.error?.code === 41) {
          this._handleSurveyAlreadyCompleted(survey);
          return;
        }

        this.validationError.set(error?.error?.message ?? 'Unable to complete the survey. Please try again.');
      },
    });
  }

  goToDashboard(): void {
    this._dialogRef.close(true);
  }

  private _loadSurvey(): void {
    this.surveyViewState.set('loading');
    this.loadError.set(null);

    this._bonusSurveyService.loadSurvey(this.surveyId(), { forceRefresh: true }).pipe(take(1)).subscribe({
      next: (survey) => {
        this._survey.set(survey);
        this.surveyLoaded.emit(survey);

        if (survey.response?.status === 'COMPLETED') {
          this._handleSurveyAlreadyCompleted(survey);
          this.surveyLoadFinished.emit();
          return;
        }

        if (survey.response?.status === 'IN_PROGRESS') {
          const restoredAnswers = new Map<number, SurveyAnswerDto>();

          for (const answer of survey.response.answers ?? []) {
            const question = getSurveyQuestionById(survey, answer.questionId);
            // Skip empty/cleared answers so the step stays unanswered.
            if (!isSurveyAnswerProvided(answer, question)) {
              continue;
            }

            restoredAnswers.set(answer.questionId, answer);
          }

          this._answers.set(restoredAnswers);
        }

        const questions = getSurveyQuestions(survey);
        if (!questions.length) {
          this.loadError.set('This survey has no questions yet.');
          this.surveyViewState.set('error');
          this.surveyLoadFinished.emit();
          return;
        }

        const resumeQuestionId = findResumeQuestionId(survey, this._answers());
        if (resumeQuestionId === null) {
          this.loadError.set('This survey has no questions yet.');
          this.surveyViewState.set('error');
          this.surveyLoadFinished.emit();
          return;
        }
        const path = buildAnswerPath(survey, this._answers(), resumeQuestionId);
        this.visitedQuestionIds.set(path);
        this.currentQuestionId.set(resumeQuestionId);
        this._syncControlWithCurrentQuestion();
        this.surveyViewState.set('active');
        this.surveyLoadFinished.emit();
      },
      error: (error) => {
        if (error?.status === 404) {
          this.loadError.set('This survey is no longer available.');
          this.surveyViewState.set('error');
          this._variablesService.triggerBannersReload();
          this.surveyLoadFinished.emit();
          return;
        }

        this.loadError.set(error?.error?.message ?? 'Failed to load survey. Please try again later.');
        this.surveyViewState.set('error');
        this.surveyLoadFinished.emit();
      },
    });
  }

  private _storeAnswer(questionId: number, answer: SurveyAnswerDto): void {
    this._answers.update((answers) => new Map(answers).set(questionId, answer));
  }

  private _removeAnswer(questionId: number): void {
    this._answers.update((answers) => {
      const next = new Map(answers);
      next.delete(questionId);
      return next;
    });
  }

  private _goToQuestion(questionId: number): void {
    const path = this.visitedQuestionIds();
    const existingIndex = path.indexOf(questionId);

    // A loop back into an already visited question truncates the path so the
    // step number stays consistent with what the user sees.
    this.visitedQuestionIds.set(
      existingIndex >= 0 ? path.slice(0, existingIndex + 1) : [...path, questionId],
    );
    this.currentQuestionId.set(questionId);
    this._syncControlWithCurrentQuestion();
  }

  private _syncCurrentAnswerToServer(): void {
    const question = this.currentQuestion();
    const survey = this._survey();
    if (!question || !survey || this.isReadOnly()) {
      return;
    }

    const value = this.stepControl.value;
    const customText = this.customTextControl.value;
    const isProvided = this._isStepAnswerProvided(question, value, customText);
    const answer = isProvided
      ? buildSurveyAnswer(question, value, customText)
      : buildClearedSurveyAnswer(question);

    if (isProvided) {
      this._storeAnswer(question.id, answer);
    } else {
      this._removeAnswer(question.id);
    }

    this._apiService.postSurveyAnswers(survey.id, { answers: [answer] }).pipe(take(1)).subscribe({
      next: () => this._bonusSurveyService.invalidateSurveyCache(survey.id),
      error: () => undefined,
    });
  }

  private _validateCurrentStep(question: SurveyQuestionDto): boolean {
    this._syncCustomTextValidators();

    if (question.required && this.stepControl.invalid) {
      this.stepControl.markAsTouched();
      this.stepControl.updateValueAndValidity();
      return false;
    }

    if (isCustomTextOptionSelected(question, this.stepControl.value) && this.customTextControl.invalid) {
      this.customTextControl.markAsTouched();
      this.customTextControl.updateValueAndValidity();
      return false;
    }

    return true;
  }

  private _isStepAnswerProvided(
    question: SurveyQuestionDto,
    value: StepControlValue,
    customText: string,
  ): boolean {
    if (question.type === 'MULTI_CHOICE') {
      if (!Array.isArray(value) || value.length === 0) {
        return false;
      }
    } else if (isSingleSelectQuestionType(question.type)) {
      if (value === '' || value === null || value === undefined) {
        return false;
      }
    } else {
      return String(value).trim().length > 0;
    }

    if (isCustomTextOptionSelected(question, value)) {
      return String(customText).trim().length > 0;
    }

    return true;
  }

  private _syncControlWithCurrentQuestion(): void {
    const question = this.currentQuestion();
    if (!question) {
      return;
    }

    const savedAnswer = this._answers().get(question.id);
    const validators = question.required ? [Validators.required] : [];

    if (question.type === 'MULTI_CHOICE') {
      this.stepControl.setValidators(validators);
      this.stepControl.setValue(savedAnswer?.optionIds ?? []);
    } else if (isSingleSelectQuestionType(question.type)) {
      this.stepControl.setValidators(validators);
      this.stepControl.setValue(savedAnswer?.optionIds?.[0] ?? '');
    } else {
      this.stepControl.setValidators(validators);
      this.stepControl.setValue(savedAnswer?.textValue ?? '');
    }

    this.customTextControl.setValue(savedAnswer?.textValue ?? '');
    this.customTextControl.markAsPristine();
    this.customTextControl.markAsUntouched();
    this._syncCustomTextValidators();

    this.stepControl.markAsPristine();
    this.stepControl.markAsUntouched();
    this.stepControl.updateValueAndValidity({ emitEvent: true });
    this._syncControlDisabledState();
  }

  private _syncCustomTextValidators(): void {
    const question = this.currentQuestion();
    const needsCustomText = isCustomTextOptionSelected(question, this.stepControl.value);

    if (needsCustomText) {
      this.customTextControl.setValidators([Validators.required]);
    } else {
      this.customTextControl.clearValidators();
    }

    this.customTextControl.updateValueAndValidity({ emitEvent: true });
  }

  private _syncControlDisabledState(): void {
    if (this.isReadOnly()) {
      this.stepControl.disable({ emitEvent: false });
      this.customTextControl.disable({ emitEvent: false });
      return;
    }

    this.stepControl.enable({ emitEvent: false });
    this.customTextControl.enable({ emitEvent: false });
  }

  private _handleSurveyAlreadyCompleted(survey: SurveyDto): void {
    this.completedRewardAmount.set(this._bonusSurveyService.resolveDisplayRewardAmount(survey));
    this.surveyViewState.set('completed');
    this._syncControlDisabledState();
    this._variablesService.triggerBannersReload();
  }
}
